<script setup lang="ts">
import { ref, computed } from 'vue'
import { getWhtrRiskLabel } from '../lib/scoringHelpers'
import { isFieldEntered, SCORING_FIELD_PLACEHOLDER } from '../lib/scoringInput'

const waist  = defineModel<string>('waist',  { required: true })
const height = defineModel<string>('height', { required: true })
const exempt = defineModel<boolean>('exempt', { required: true })

const props = defineProps<{ componentScore: number | null }>()

const isOpen = ref(false)

const rawRatio = computed(() => {
  const w = parseFloat(waist.value)
  const h = parseFloat(height.value)
  if (!w || !h || w <= 0 || h <= 0) return null
  return w / h
})

const truncRatio = computed(() => {
  if (rawRatio.value === null) return null
  return Math.floor(rawRatio.value * 100) / 100
})

const riskInfo = computed(() => {
  if (truncRatio.value === null) return null
  return getWhtrRiskLabel(truncRatio.value)
})

const hasInput = computed(() => isFieldEntered(waist.value) && isFieldEntered(height.value))

const pillText = computed(() => {
  if (exempt.value) return 'EXEMPT'
  if (!hasInput.value) return '–'
  if (props.componentScore !== null) return props.componentScore.toFixed(1) + ' pts'
  return '–'
})
</script>

<template>
  <div class="acc-card">
    <div class="acc-header" @click="isOpen = !isOpen">
      <div class="acc-header-left">
        <span class="acc-title acc-title--brand">WHtR</span>
        <span class="acc-pts">max 20 pts</span>
      </div>
      <label class="acc-exempt" @click.stop>
        <input type="checkbox" v-model="exempt" style="accent-color:var(--accent);width:14px;height:14px;" />
        Exempt
      </label>
      <span class="acc-score-pill" :class="{ 'has-score': !exempt && hasInput && componentScore !== null }">
        {{ pillText }}
      </span>
      <span class="acc-tap-hint">{{ isOpen ? 'tap to collapse' : 'tap to expand' }}</span>
      <span class="acc-chevron" :class="{ open: isOpen }" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>
    </div>

    <div v-show="isOpen" class="acc-body">
      <div :style="exempt ? { opacity: '0.35', pointerEvents: 'none' } : {}">
        <div class="row">
          <div class="field">
            <label>Waist (inches)</label>
            <input type="text" inputmode="decimal" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="waist" aria-label="Waist in inches" />
          </div>
          <div class="field">
            <label>Height (inches)</label>
            <input type="text" inputmode="decimal" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="height" aria-label="Height in inches" />
          </div>
          <div v-if="rawRatio !== null" class="field">
            <label>WHtR</label>
            <input type="text" readonly :value="rawRatio.toFixed(3)" placeholder="Auto-calculated" aria-label="Calculated waist to height ratio" />
          </div>
        </div>
        <div v-if="riskInfo !== null" style="margin-top:4px;font-size:12px;">
          <span :style="{ color: riskInfo.color }">{{ riskInfo.label }}</span>
          <span style="color:#556;margin-left:6px;">(ratio {{ truncRatio?.toFixed(2) }})</span>
        </div>
      </div>
    </div>
  </div>
</template>
